// Backward scan from the cursor to find the most-recently-completed column
// reference operand. Returns `null` when no clear column operand is visible
// (e.g. cursor at start of clause, immediately after another operator, or
// after a literal/expression we can't classify cheaply).
//
// Slice C3 of `Ralph/JsonColumnSemantics/PRD.md`: this is the cheap entry
// point for the IntellisenseEngine's per-engine resolvers to decide whether
// to surface JSON-typed operators. The PG/MySQL resolvers used to emit JSON
// operators unconditionally at every expression position; v1 gates emission
// on whether the immediately-preceding identifier resolves to a JSON-classified
// column. If we can't make that call (no operand visible, no schema match),
// we return `null` and the caller emits no JSON operators — strictly more
// conservative than the v0 over-surfacing behaviour.

import type { AliasBinding } from '@/core/engines/intellisenseTypes';
import type { JsonClassification } from '@/features/sql-resolver/types/schema';

// Structural minimum the operand-classifier needs. Resolvers pass their own
// SnapshotTable shape (a partial of TableMeta); this keeps the helper engine-
// agnostic without forcing each resolver to widen its types.
export type MinimalColumn = {
  readonly name: string;
  readonly dataType: string;
  readonly jsonClassification?: JsonClassification;
};

export type MinimalTable = {
  readonly name: string;
  readonly schema?: string;
  readonly columns: ReadonlyArray<MinimalColumn>;
};

export type PrecedingColumnOperand = {
  readonly column: MinimalColumn;
  readonly jsonClassification: JsonClassification | undefined;
  readonly via: 'alias' | 'unqualified' | 'schema-qualified';
};

/**
 * `fold` — per-engine identifier fold ('lower' for PG / MySQL / SQLite,
 * 'upper' for Oracle, identity for MSSQL).
 */
export type FindOperandArgs = {
  readonly text: string;
  readonly offset: number;
  readonly statementStart: number;
  readonly aliasBindings: readonly AliasBinding[];
  readonly snapshotTables: ReadonlyArray<MinimalTable>;
  readonly fold: (s: string) => string;
};

/**
 * Walk back from `offset` over whitespace, then over a single dotted
 * identifier chain (`<col>`, `<alias>.<col>`, `<schema>.<table>.<col>`).
 * Returns the parts in source order (leftmost = first). `null` when no
 * identifier is visible at the cursor's immediate left.
 */
function readBackwardDottedRef(
  text: string,
  offset: number,
  statementStart: number,
): readonly string[] | null {
  let pos = offset;
  while (pos > statementStart && isWhitespaceChar(text.charCodeAt(pos - 1))) pos -= 1;
  if (pos === statementStart) return null;

  const parts: string[] = [];
  for (;;) {
    const segment = readBackwardIdentifier(text, pos, statementStart);
    if (!segment) break;
    parts.unshift(segment.content);
    pos = segment.start;
    let dotPos = pos;
    while (dotPos > statementStart && isWhitespaceChar(text.charCodeAt(dotPos - 1))) {
      dotPos -= 1;
    }
    if (dotPos === statementStart || text.charCodeAt(dotPos - 1) !== 0x2e /* . */) break;
    pos = dotPos - 1;
    while (pos > statementStart && isWhitespaceChar(text.charCodeAt(pos - 1))) pos -= 1;
  }
  return parts.length > 0 ? parts : null;
}

export type IdentifierSegment = { readonly start: number; readonly content: string };

export function readBackwardIdentifier(
  text: string,
  end: number,
  limit: number,
): IdentifierSegment | null {
  if (end <= limit) return null;
  const last = text.charCodeAt(end - 1);
  if (last === 0x5d /* ] */) return readBackwardQuoted(text, end, limit, 0x5b, 0x5d);
  if (last === 0x22 /* " */) return readBackwardQuoted(text, end, limit, 0x22, 0x22);
  if (last === 0x60 /* ` */) return readBackwardQuoted(text, end, limit, 0x60, 0x60);

  let start = end;
  while (start > limit && isBareIdentChar(text.charCodeAt(start - 1))) start -= 1;
  if (start === end) return null;
  // Reject pure-digit runs — `123` is a numeric literal, not an identifier.
  // (Identifiers can contain digits but cannot start with one in any of the
  // dialects we support; this also guards against numeric LHS being misread.)
  if (isAsciiDigit(text.charCodeAt(start))) return null;
  return { start, content: text.slice(start, end) };
}

function readBackwardQuoted(
  text: string,
  end: number,
  limit: number,
  openCode: number,
  closeCode: number,
): IdentifierSegment | null {
  let i = end - 2;
  while (i >= limit) {
    if (text.charCodeAt(i) === openCode) {
      // Symmetric quotes (`"`, `` ` ``) use doubled-escape; skip over them.
      if (openCode === closeCode && i > limit && text.charCodeAt(i - 1) === closeCode) {
        i -= 2;
        continue;
      }
      const content = text.slice(i + 1, end - 1);
      return { start: i, content };
    }
    i -= 1;
  }
  return null;
}

export function isWhitespaceChar(code: number): boolean {
  return code === 0x20 || code === 0x09 || code === 0x0a || code === 0x0d;
}

function isBareIdentChar(code: number): boolean {
  return (
    (code >= 0x41 && code <= 0x5a) /* A-Z */ ||
    (code >= 0x61 && code <= 0x7a) /* a-z */ ||
    (code >= 0x30 && code <= 0x39) /* 0-9 */ ||
    code === 0x5f /* _ */ ||
    code === 0x24 /* $ */
  );
}

function isAsciiDigit(code: number): boolean {
  return code >= 0x30 && code <= 0x39;
}

/**
 * Verify the character immediately preceding `parts` was a statement-level
 * boundary that legitimately precedes a column operand. Specifically: we
 * accept the column ref only if it's preceded by whitespace + one of:
 *   - a comparison/binary operator keyword (`AND`, `OR`, `WHERE`, `ON`, `HAVING`),
 *   - a comma (column-list / row constructor),
 *   - an open paren,
 *   - statement start.
 * Rejects refs that follow a literal-value-shaped or function-call shape
 * (`=`, `<`, `(`-after-ident pairing) — those mean the cursor is in the
 * RHS of a comparison, which produces a value rather than a column-operator.
 */
export function isAtOperandBoundary(text: string, before: number, statementStart: number): boolean {
  let pos = before;
  while (pos > statementStart && isWhitespaceChar(text.charCodeAt(pos - 1))) pos -= 1;
  if (pos === statementStart) return true;
  // The 3 keyword boundaries we look for. Read up to 8 chars backwards.
  const slice = text.slice(Math.max(statementStart, pos - 8), pos);
  if (/(^|[^A-Za-z0-9_])(AND|OR|ON|WHERE|HAVING|WHEN|CASE)\s*$/i.test(slice)) return true;
  const ch = text.charCodeAt(pos - 1);
  if (ch === 0x2c /* , */) return true;
  if (ch === 0x28 /* ( */) return true;
  // Comparison closing — `<rhs> OP <newRef>` is invalid SQL but a user might
  // be re-typing. Reject so we don't misclassify.
  return false;
}

export function findPrecedingColumnOperand(
  args: FindOperandArgs,
): PrecedingColumnOperand | null {
  const { text, offset, statementStart, aliasBindings, snapshotTables, fold } = args;
  const parts = readBackwardDottedRef(text, offset, statementStart);
  if (!parts || parts.length === 0 || parts.length > 3) return null;

  // Find where the leftmost part starts so we can validate the upstream
  // boundary. Walk back from offset to skip whitespace + the dotted-ref text.
  let walkBack = offset;
  while (walkBack > statementStart && isWhitespaceChar(text.charCodeAt(walkBack - 1))) {
    walkBack -= 1;
  }
  // Re-scan to find the leftmost ident start. We trust the parts we already
  // produced — compute by repeatedly stepping over an ident + an optional dot.
  let p = walkBack;
  for (let i = 0; i < parts.length; i += 1) {
    const seg = readBackwardIdentifier(text, p, statementStart);
    if (!seg) return null;
    p = seg.start;
    if (i < parts.length - 1) {
      let dotPos = p;
      while (dotPos > statementStart && isWhitespaceChar(text.charCodeAt(dotPos - 1))) {
        dotPos -= 1;
      }
      if (dotPos === statementStart || text.charCodeAt(dotPos - 1) !== 0x2e) return null;
      p = dotPos - 1;
      while (p > statementStart && isWhitespaceChar(text.charCodeAt(p - 1))) p -= 1;
    }
  }
  if (!isAtOperandBoundary(text, p, statementStart)) return null;

  // Resolution — try (a) <col> via aliases, (b) <alias>.<col>, (c) <schema>.<table>.<col>.
  if (parts.length === 1) {
    const col = parts[0]!;
    return resolveUnqualifiedColumn(col, aliasBindings, snapshotTables, fold);
  }
  if (parts.length === 2) {
    return resolveAliasColumn(parts[0]!, parts[1]!, aliasBindings, snapshotTables, fold);
  }
  // 3 parts: schema.table.column (skip — alias-only and schema-qualified are
  // the common ones; 3-part column refs are rare and v1 doesn't classify them).
  return resolveSchemaQualifiedColumn(
    parts[0]!,
    parts[1]!,
    parts[2]!,
    snapshotTables,
    fold,
  );
}

function resolveUnqualifiedColumn(
  rawCol: string,
  aliases: readonly AliasBinding[],
  tables: ReadonlyArray<MinimalTable>,
  fold: (s: string) => string,
): PrecedingColumnOperand | null {
  const colFolded = fold(stripQuotes(rawCol));
  // Walk aliases in order; first match wins (matches existing column-resolve
  // posture across the per-engine resolvers).
  for (const ab of aliases) {
    const tbl = matchTable(tables, ab.relation, ab.schema, fold);
    if (!tbl) continue;
    const col = tbl.columns.find((c) => fold(c.name) === colFolded);
    if (col) {
      return { column: col, jsonClassification: col.jsonClassification, via: 'unqualified' };
    }
  }
  return null;
}

function resolveAliasColumn(
  rawAlias: string,
  rawCol: string,
  aliases: readonly AliasBinding[],
  tables: ReadonlyArray<MinimalTable>,
  fold: (s: string) => string,
): PrecedingColumnOperand | null {
  const aliasFolded = fold(stripQuotes(rawAlias));
  const colFolded = fold(stripQuotes(rawCol));
  const ab = aliases.find((b) => fold(b.alias) === aliasFolded);
  if (!ab) {
    // May be `schema.table.col` where parts[0] is schema, parts[1] is table.
    // But that's 3 parts — this is 2, so could also be `tableName.col`.
    const tbl = matchTable(tables, aliasFolded, undefined, fold);
    if (!tbl) return null;
    const col = tbl.columns.find((c) => fold(c.name) === colFolded);
    if (!col) return null;
    return { column: col, jsonClassification: col.jsonClassification, via: 'alias' };
  }
  const tbl = matchTable(tables, ab.relation, ab.schema, fold);
  if (!tbl) return null;
  const col = tbl.columns.find((c) => fold(c.name) === colFolded);
  if (!col) return null;
  return { column: col, jsonClassification: col.jsonClassification, via: 'alias' };
}

function resolveSchemaQualifiedColumn(
  rawSchema: string,
  rawTable: string,
  rawCol: string,
  tables: ReadonlyArray<MinimalTable>,
  fold: (s: string) => string,
): PrecedingColumnOperand | null {
  const sFolded = fold(stripQuotes(rawSchema));
  const tFolded = fold(stripQuotes(rawTable));
  const colFolded = fold(stripQuotes(rawCol));
  const tbl = tables.find(
    (t) => fold(t.name) === tFolded && fold(t.schema ?? '') === sFolded,
  );
  if (!tbl) return null;
  const col = tbl.columns.find((c) => fold(c.name) === colFolded);
  if (!col) return null;
  return { column: col, jsonClassification: col.jsonClassification, via: 'schema-qualified' };
}

function matchTable(
  tables: ReadonlyArray<MinimalTable>,
  relation: string,
  schema: string | undefined,
  fold: (s: string) => string,
): MinimalTable | undefined {
  const r = fold(stripQuotes(relation));
  const s = schema ? fold(stripQuotes(schema)) : undefined;
  return tables.find((t) => {
    if (fold(t.name) !== r) return false;
    if (s === undefined) return true;
    return fold(t.schema ?? '') === s;
  });
}

function stripQuotes(s: string): string {
  if (s.length < 2) return s;
  const first = s.charCodeAt(0);
  const last = s.charCodeAt(s.length - 1);
  if (
    (first === 0x22 && last === 0x22) ||
    (first === 0x60 && last === 0x60) ||
    (first === 0x5b && last === 0x5d)
  ) {
    return s.slice(1, -1);
  }
  return s;
}
